import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Heart, Share2, MapPin, Star, Clock, Tag, 
  MessageCircle, Repeat, DollarSign, User, ChevronLeft, ChevronRight,
  Check, AlertCircle, BadgeCheck, Shield, Eye, TrendingUp, Loader2
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { api, useAuth, formatApiErrorDetail } from '../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import TradeProposalDialog from '../components/TradeProposalDialog';
import { getCategoryLabel, getSubcategoryLabel } from '../lib/categories';

const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1612734425688-d7a59377e5f7?w=800',
  'https://images.unsplash.com/photo-1610375461246-83df859d849d?w=800',
  'https://images.unsplash.com/photo-1633869699811-cd4f63049b36?w=800',
];

export default function ListingDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [offerAmount, setOfferAmount] = useState('');
  const [offerMessage, setOfferMessage] = useState('');
  const [bidAmount, setBidAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showOfferDialog, setShowOfferDialog] = useState(false);
  const [showBidDialog, setShowBidDialog] = useState(false);
  const [showTradeDialog, setShowTradeDialog] = useState(false);
  const [buyingNow, setBuyingNow] = useState(false);
  const [showBuyConfirm, setShowBuyConfirm] = useState(false);
  const [feePreview, setFeePreview] = useState(null);
  const [loadingFees, setLoadingFees] = useState(false);

  useEffect(() => {
    const fetchListing = async () => {
      try {
        const { data } = await api.get(`/listings/${id}`);
        setListing(data);
      } catch {
        toast.error('Listing not found');
        navigate('/marketplace');
      } finally {
        setLoading(false);
      }
    };
    fetchListing();
  }, [id, navigate]);

  const images = listing?.images?.length > 0 ? listing.images : PLACEHOLDER_IMAGES.slice(0, 1);

  const handleMakeOffer = async () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/listings/${id}/offers`, {
        amount: parseFloat(offerAmount),
        message: offerMessage
      });
      toast.success('Offer sent successfully!');
      setShowOfferDialog(false);
      setOfferAmount('');
      setOfferMessage('');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePlaceBid = async () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(`/listings/${id}/bids`, {
        amount: parseFloat(bidAmount)
      });
      toast.success('Bid placed successfully!');
      setShowBidDialog(false);
      setBidAmount('');
      // Refresh listing to get updated bid
      const { data } = await api.get(`/listings/${id}`);
      setListing(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleContactSeller = () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }
    navigate('/messages', { state: { receiverId: listing.seller_id, listingId: listing.id } });
  };

  const handleBuyNow = async () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/listing/${id}` } } });
      return;
    }
    // Show confirmation with fee breakdown
    setLoadingFees(true);
    setShowBuyConfirm(true);
    try {
      const { data } = await api.get(`/checkout/fee-preview/${id}`);
      setFeePreview(data);
    } catch {
      setFeePreview(null);
    } finally {
      setLoadingFees(false);
    }
  };

  const confirmBuyNow = async () => {
    setBuyingNow(true);
    try {
      const { data } = await api.post('/checkout/buy-now', {
        listing_id: id,
        origin_url: window.location.origin
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBuyingNow(false);
      setShowBuyConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-base">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="aspect-square bg-surface-card animate-pulse" />
            <div className="space-y-4">
              <div className="h-8 bg-surface-card animate-pulse w-3/4" />
              <div className="h-6 bg-surface-card animate-pulse w-1/2" />
              <div className="h-20 bg-surface-card animate-pulse" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!listing) return null;

  const isOwner = user?.id === listing.seller_id;
  const isAuction = listing.listing_type === 'auction';
  const currentPrice = isAuction ? (listing.current_bid || listing.starting_bid) : listing.price;

  return (
    <div className="min-h-screen bg-surface-base">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back button */}
        <Link 
          to="/marketplace" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Marketplace
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Images */}
          <div className="space-y-4">
            <div className="relative aspect-square bg-surface-card overflow-hidden group">
              <img
                src={images[currentImageIndex].startsWith('/api') 
                  ? `${process.env.REACT_APP_BACKEND_URL}${images[currentImageIndex]}`
                  : images[currentImageIndex]}
                alt={listing.title}
                className="w-full h-full object-cover"
              />
              
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentImageIndex(i => (i - 1 + images.length) % images.length)}
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronLeft className="w-6 h-6 text-white" />
                  </button>
                  <button
                    onClick={() => setCurrentImageIndex(i => (i + 1) % images.length)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <ChevronRight className="w-6 h-6 text-white" />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {isAuction ? (
                  <span className="badge-auction px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                    Auction
                  </span>
                ) : (
                  <span className="badge-buy-now px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                    Buy Now
                  </span>
                )}
                {listing.trade_enabled && (
                  <span className="badge-trade px-3 py-1 text-xs uppercase tracking-wider font-semibold">
                    Trade
                  </span>
                )}
              </div>
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2">
                {images.map((img, idx) => (
                  <button
                    key={img}
                    onClick={() => setCurrentImageIndex(idx)}
                    className={`flex-shrink-0 w-20 h-20 border-2 transition-colors ${
                      idx === currentImageIndex ? 'border-gold' : 'border-border-subtle'
                    }`}
                  >
                    <img
                      src={img.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <p className="text-xs tracking-[0.2em] uppercase text-gold font-semibold">
                  {getCategoryLabel(listing.category)}
                  {listing.subcategory && ` / ${getSubcategoryLabel(listing.category, listing.subcategory)}`}
                </p>
                {listing.views > 10 && (
                  <span className="flex items-center gap-1 text-xs text-zinc-500">
                    <Eye className="w-3 h-3" /> {listing.views} views
                  </span>
                )}
              </div>
              <h1 className="font-clash text-3xl sm:text-4xl font-medium text-white mb-4" data-testid="listing-title">
                {listing.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-zinc-400">
                {listing.location && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" /> {listing.location}
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <Tag className="w-4 h-4" /> {listing.condition.replace('_', ' ')}
                </span>
                {listing.trade_enabled && (
                  <span className="flex items-center gap-1 text-gold">
                    <Repeat className="w-4 h-4" /> Trade Enabled
                  </span>
                )}
              </div>
            </div>

            {/* Trust Elements */}
            <div className="flex flex-wrap gap-3">
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 border border-emerald-500/20">
                <Shield className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-emerald-500 font-medium">Buyer Protection</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 border border-blue-500/20">
                <BadgeCheck className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-blue-500 font-medium">Secure Transaction</span>
              </div>
            </div>

            {/* Price */}
            <div className="p-6 bg-surface-card border border-border-subtle">
              {isAuction ? (
                <div>
                  <p className="text-sm text-zinc-400 mb-1">
                    {listing.current_bid ? 'Current Bid' : 'Starting Bid'}
                  </p>
                  <p className="font-clash text-4xl font-semibold gold-text" data-testid="listing-price">
                    ${currentPrice?.toLocaleString()}
                  </p>
                  {listing.bid_count > 0 && (
                    <p className="text-sm text-zinc-400 mt-2 flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-emerald-500" /> {listing.bid_count} bids
                    </p>
                  )}
                  {listing.auction_end_date && (
                    <div className="flex items-center gap-2 mt-4 text-sm text-zinc-400">
                      <Clock className="w-4 h-4" />
                      Ends: {new Date(listing.auction_end_date).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-sm text-zinc-400 mb-1">Price</p>
                  <p className="font-clash text-4xl font-semibold gold-text" data-testid="listing-price">
                    ${listing.price?.toLocaleString()}
                  </p>
                </div>
              )}
            </div>

            {/* Actions */}
            {!isOwner && (
              <div className="space-y-3">
                {/* Trade Button - Prominent if enabled */}
                {listing.trade_enabled && (
                  <button
                    onClick={() => {
                      if (!user) {
                        navigate('/login', { state: { from: { pathname: `/listing/${id}` } } });
                        return;
                      }
                      setShowTradeDialog(true);
                    }}
                    className="w-full py-4 flex items-center justify-center gap-3 bg-gradient-to-r from-gold/20 to-gold/10 border-2 border-gold text-gold font-semibold uppercase tracking-wider text-sm hover:bg-gold hover:text-black transition-all duration-300"
                    data-testid="propose-trade-btn"
                  >
                    <Repeat className="w-5 h-5" /> Propose a Trade
                  </button>
                )}

                {isAuction ? (
                  <Dialog open={showBidDialog} onOpenChange={setShowBidDialog}>
                    <DialogTrigger asChild>
                      <button className="btn-gold w-full py-4" data-testid="place-bid-btn">
                        Place Bid
                      </button>
                    </DialogTrigger>
                    <DialogContent className="bg-surface-card border-border-subtle">
                      <DialogHeader>
                        <DialogTitle className="font-clash text-xl text-white">Place a Bid</DialogTitle>
                        <DialogDescription className="sr-only">Place a bid on this listing</DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <p className="text-sm text-zinc-400">
                          Current bid: ${currentPrice?.toLocaleString()}
                        </p>
                        <input
                          type="number"
                          value={bidAmount}
                          onChange={(e) => setBidAmount(e.target.value)}
                          placeholder={`Enter amount above $${currentPrice}`}
                          className="input-dark w-full px-4 py-3"
                        />
                        <button
                          onClick={handlePlaceBid}
                          disabled={submitting || !bidAmount || parseFloat(bidAmount) <= currentPrice}
                          className="btn-gold w-full py-3 disabled:opacity-50"
                        >
                          {submitting ? 'Placing Bid...' : 'Confirm Bid'}
                        </button>
                      </div>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <button
                    onClick={handleBuyNow}
                    disabled={buyingNow}
                    className="btn-gold w-full py-4 flex items-center justify-center gap-2 disabled:opacity-50"
                    data-testid="buy-now-btn"
                  >
                    {buyingNow ? (
                      <><Loader2 className="w-5 h-5 animate-spin" /> Processing...</>
                    ) : (
                      'Buy Now'
                    )}
                  </button>
                )}

                <Dialog open={showOfferDialog} onOpenChange={setShowOfferDialog}>
                  <DialogTrigger asChild>
                    <button className="btn-secondary w-full py-4 flex items-center justify-center gap-2" data-testid="make-offer-btn">
                      <DollarSign className="w-5 h-5" /> Make an Offer
                    </button>
                  </DialogTrigger>
                  <DialogContent className="bg-surface-card border-border-subtle">
                    <DialogHeader>
                      <DialogTitle className="font-clash text-xl text-white">Make an Offer</DialogTitle>
                      <DialogDescription className="sr-only">Make an offer on this listing</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-2">Your Offer</label>
                        <input
                          type="number"
                          value={offerAmount}
                          onChange={(e) => setOfferAmount(e.target.value)}
                          placeholder="Enter your offer amount"
                          className="input-dark w-full px-4 py-3"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-zinc-300 mb-2">Message (optional)</label>
                        <textarea
                          value={offerMessage}
                          onChange={(e) => setOfferMessage(e.target.value)}
                          placeholder="Add a message to the seller"
                          className="input-dark w-full px-4 py-3 h-24 resize-none"
                        />
                      </div>
                      <button
                        onClick={handleMakeOffer}
                        disabled={submitting || !offerAmount}
                        className="btn-gold w-full py-3 disabled:opacity-50"
                      >
                        {submitting ? 'Sending...' : 'Send Offer'}
                      </button>
                    </div>
                  </DialogContent>
                </Dialog>

                <button
                  onClick={handleContactSeller}
                  className="btn-secondary w-full py-4 flex items-center justify-center gap-2"
                  data-testid="contact-seller-btn"
                >
                  <MessageCircle className="w-5 h-5" /> Contact Seller
                </button>
              </div>
            )}

            {isOwner && (
              <div className="p-4 bg-surface-elevated border border-gold/20 flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-gold" />
                <span className="text-sm text-zinc-300">This is your listing</span>
              </div>
            )}

            {/* Description */}
            <div>
              <h2 className="font-clash text-lg font-medium text-white mb-3">Description</h2>
              <p className="text-zinc-400 leading-relaxed whitespace-pre-wrap">
                {listing.description}
              </p>
            </div>

            {/* Card Details */}
            {(listing.player_character || listing.set_series || listing.year || listing.is_graded) && (
              <div className="p-4 bg-surface-card border border-border-subtle">
                <h2 className="font-clash text-lg font-medium text-white mb-3">Card Details</h2>
                <div className="grid grid-cols-2 gap-3">
                  {listing.player_character && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Player / Character</p>
                      <p className="text-sm text-white mt-1">{listing.player_character}</p>
                    </div>
                  )}
                  {listing.set_series && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Set / Series</p>
                      <p className="text-sm text-white mt-1">{listing.set_series}</p>
                    </div>
                  )}
                  {listing.year && (
                    <div>
                      <p className="text-xs text-zinc-500 uppercase tracking-wider">Year</p>
                      <p className="text-sm text-white mt-1">{listing.year}</p>
                    </div>
                  )}
                  {listing.is_graded && (
                    <>
                      {listing.grading_company && (
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Grading Company</p>
                          <p className="text-sm text-white mt-1">{listing.grading_company}</p>
                        </div>
                      )}
                      {listing.grade && (
                        <div>
                          <p className="text-xs text-zinc-500 uppercase tracking-wider">Grade</p>
                          <p className="text-sm text-gold font-semibold mt-1">{listing.grade}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Trade Preferences */}
            {listing.trade_enabled && listing.trade_preferences && (
              <div className="p-4 bg-gold/5 border border-gold/20">
                <h2 className="font-clash text-lg font-medium text-white mb-3 flex items-center gap-2">
                  <Repeat className="w-5 h-5 text-gold" /> Trade Preferences
                </h2>
                <p className="text-zinc-400 leading-relaxed">
                  {listing.trade_preferences}
                </p>
              </div>
            )}

            {/* Seller Info */}
            {listing.seller && (
              <div className="p-6 bg-surface-card border border-border-subtle">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-clash text-lg font-medium text-white">Seller</h2>
                  {listing.seller.is_verified && (
                    <span className="flex items-center gap-1 text-xs text-emerald-500 bg-emerald-500/10 px-2 py-1 border border-emerald-500/20">
                      <BadgeCheck className="w-3 h-3" /> Verified Seller
                    </span>
                  )}
                </div>
                <Link 
                  to={`/user/${listing.seller_id}`}
                  className="flex items-center gap-4 hover:bg-surface-elevated p-3 -m-3 transition-colors group"
                >
                  <div className="w-16 h-16 bg-gold/10 flex items-center justify-center">
                    {listing.seller.avatar_url ? (
                      <img src={listing.seller.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-8 h-8 text-gold" />
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-white group-hover:text-gold transition-colors">{listing.seller.name}</p>
                    <div className="flex items-center gap-2 text-sm text-zinc-400 mt-1">
                      <Star className="w-4 h-4 text-gold fill-gold" />
                      <span className="font-medium">{listing.seller.rating?.toFixed(1) || '0.0'}</span>
                      <span>({listing.seller.review_count || 0} reviews)</span>
                    </div>
                    <p className="text-xs text-zinc-500 mt-1">
                      Member since {new Date(listing.seller.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="text-zinc-500 group-hover:text-gold transition-colors">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                </Link>
              </div>
            )}

            {/* Share */}
            <div className="flex items-center gap-4 pt-4 border-t border-border-subtle">
              <button className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
                <Heart className="w-5 h-5" /> Save
              </button>
              <button className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
                <Share2 className="w-5 h-5" /> Share
              </button>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Buy Now Confirmation Dialog */}
      <Dialog open={showBuyConfirm} onOpenChange={(v) => { if (!buyingNow) setShowBuyConfirm(v); }}>
        <DialogContent className="bg-surface-card border-border-subtle max-w-md">
          <DialogHeader>
            <DialogTitle className="font-clash text-xl text-white">Confirm Purchase</DialogTitle>
            <DialogDescription className="sr-only">Review fees and confirm your purchase</DialogDescription>
          </DialogHeader>
          
          {loadingFees ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 text-gold animate-spin" /></div>
          ) : feePreview ? (
            <div className="space-y-4" data-testid="buy-confirm-dialog">
              <div className="space-y-3 p-4 bg-surface-elevated border border-border-subtle">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Item Price</span>
                  <span className="text-white font-medium">${feePreview.item_price?.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Service Fee ({feePreview.buyer_fee_pct}%)</span>
                  <span className="text-white">${feePreview.buyer_fee?.toLocaleString()}</span>
                </div>
                <div className="border-t border-border-subtle pt-3 flex justify-between">
                  <span className="text-white font-semibold">Total</span>
                  <span className="text-gold font-clash text-lg font-bold">${feePreview.total_charge?.toLocaleString()}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 px-1">
                <Shield className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <p className="text-xs text-zinc-400">Secure payment processed via Stripe</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowBuyConfirm(false)}
                  disabled={buyingNow}
                  className="btn-secondary flex-1 py-3"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmBuyNow}
                  disabled={buyingNow}
                  className="btn-gold flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="confirm-buy-btn"
                >
                  {buyingNow ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Pay Now'}
                </button>
              </div>
            </div>
          ) : (
            <p className="text-zinc-400 text-sm py-4">Unable to load fee details. Please try again.</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Trade Proposal Dialog */}
      {listing && (
        <TradeProposalDialog
          open={showTradeDialog}
          onOpenChange={setShowTradeDialog}
          listing={listing}
        />
      )}
    </div>
  );
}
